import { api } from "@/lib/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Input,
  Label,
} from "@a7y/tailwind-catalyst";
import * as Headless from "@headlessui/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import type {
  ProductEmailInputMode,
  ProductEmailSenderIdentity,
} from "../types";

const QUICK_EMAILS = [
  "yevhen@apptonomy.ai",
  "tpf@apptonomy.ai",
  "team@apptonomy.ai",
];

interface TestSendDialogProps {
  open: boolean;
  onClose: () => void;
  subject: string;
  bodyContent: string;
  inputMode: ProductEmailInputMode;
  senderIdentity: ProductEmailSenderIdentity;
}

export function TestSendDialog({
  open,
  onClose,
  subject,
  bodyContent,
  inputMode,
  senderIdentity,
}: TestSendDialogProps) {
  const [testRecipient, setTestRecipient] = useState("");

  const sendTestMutation = useMutation({
    mutationFn: () =>
      api.sendTestEmail({
        subject,
        bodyContent,
        inputMode,
        senderIdentity,
        testRecipient,
      }),
    onSuccess: () => {
      setTestRecipient("");
      onClose();
    },
  });

  const isValid = testRecipient.includes("@") && subject.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <DialogTitle>Send Test Email</DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          <Headless.Field>
            <Label>Recipient Email</Label>
            <Input
              className="mt-2"
              type="email"
              value={testRecipient}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTestRecipient(e.target.value)
              }
              placeholder="test@example.com"
            />
          </Headless.Field>

          <div>
            <p className="mb-2 text-sm text-zinc-500 dark:text-zinc-400">
              Quick select:
            </p>
            <div className="flex flex-wrap gap-2">
              {QUICK_EMAILS.map((email) => (
                <button
                  key={email}
                  type="button"
                  onClick={() => setTestRecipient(email)}
                  className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  {email}
                </button>
              ))}
            </div>
          </div>

          {sendTestMutation.isError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {sendTestMutation.error?.message || "Failed to send test email"}
            </div>
          )}

          {sendTestMutation.isSuccess && (
            <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-400">
              Test email sent successfully!
            </div>
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="blue"
          disabled={!isValid || sendTestMutation.isPending}
          onClick={() => sendTestMutation.mutate()}
        >
          {sendTestMutation.isPending ? "Sending..." : "Send Test"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
